import { apiCall, DEFAULT_API } from '@/store/sync';

/**
 * Fehlerberichte an den Server (docs/notes/betrieb.md, `POST /fehler`).
 *
 * Bisher stand ein Fehler nur auf dem Gerät: Die Fehlerseite (ErrorBoundary)
 * zeigte Meldung und Stack, und ob je jemand den Text an den Support gab,
 * war Glückssache. Jetzt geht dieselbe Kurzfassung einmal an den Server —
 * ohne Anmeldung, ohne Antwort abzuwarten, und nie so, dass sie die
 * Fehlerseite selbst stören könnte (alles in try/catch, Fehler des Sendens
 * werden verschluckt). Die Admin-Seite listet die Berichte.
 *
 * Was **nicht** mitgeht: Turniernamen, Teams, Schlüssel. Die Seite wird zum
 * Routenmuster (`/t/:id/spiele`), der Suchteil (`?t=…&k=…`) fällt weg, der
 * Stack behält nur die ersten Zeilen ohne die Adresse des Servers.
 */
const MAX_JE_SITZUNG = 3;
let gesendet = 0;
let beobachtet = false;

/** Routenmuster aus dem Hash: IDs (Hex ab 8 Zeichen, `newId`) werden `:id`, Suchteil und alles Weitere fällt weg */
export function seitenMuster(hash: string = location.hash): string {
  const pfad = hash.replace(/^#/, '').replace(/[?].*$/, '');
  const teile = pfad
    .split('/')
    .filter(Boolean)
    .map((seg) => (/^[0-9a-f]{8,}$/i.test(seg) ? ':id' : seg));
  return `/${teile.join('/')}`;
}

/** Erste Stack-Zeilen ohne die Meldung selbst und ohne Server-Adresse — genug für „welcher Chunk, welche Zeile" */
export function stackKurz(error: { message: string; stack?: string }, zeilen = 5): string | undefined {
  const out = (error.stack ?? '')
    .split('\n')
    .filter((z) => z.trim() && !z.includes(error.message))
    .slice(0, zeilen)
    .map((z) => z.trim().replace(/https?:\/\/[^/\s)]+/g, ''));
  return out.length ? out.join('\n') : undefined;
}

/** Bericht schicken — höchstens MAX_JE_SITZUNG je Sitzung, nur mit eingebautem Server, nie wartend, nie werfend */
export function meldeFehler(fehler: unknown, art: 'render' | 'promise'): void {
  if (!DEFAULT_API || gesendet >= MAX_JE_SITZUNG) return;
  gesendet++;
  try {
    const e = fehler instanceof Error ? fehler : new Error(typeof fehler === 'string' ? fehler : 'Unbekannter Fehler');
    const body: Record<string, unknown> = {
      nachricht: (e.message || 'Unbekannter Fehler').slice(0, 500),
      seite: seitenMuster(),
      version: __APP_VERSION__,
      ua: navigator.userAgent.slice(0, 300),
      at: Date.now(),
      art,
    };
    const stack = stackKurz(e);
    if (stack) body.stack = stack;
    const sprache = document.documentElement.lang;
    if (sprache) body.sprache = sprache;
    // Kein apiCall: ohne Login-Token, ohne Token-Erneuerung — und mit keepalive, falls die Seite gleich neu lädt
    void fetch(`${DEFAULT_API.replace(/\/$/, '')}/fehler`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
  } catch {
    /* Die Fehlerseite darf daran nie scheitern */
  }
}

/** Unbehandelte Promise-Ablehnungen (fetch ohne catch, IndexedDB) einmal je Seite mitschneiden */
export function fehlerberichteBeobachten(): void {
  if (beobachtet) return;
  beobachtet = true;
  try {
    window.addEventListener('unhandledrejection', (ev) => meldeFehler(ev.reason, 'promise'));
  } catch {
    /* egal */
  }
}

/** Wie der Server ihn führt (services/sync/src/protocol.ts `Fehlerbericht`) */
export interface Fehlerbericht {
  id: string;
  at: number;
  nachricht: string;
  stack?: string;
  seite?: string;
  version?: string;
  ua?: string;
  sprache?: string;
  clientAt?: number;
  art?: 'render' | 'promise';
}

/** Admin-Seite: die letzten Berichte, neueste zuerst (`GET /admin/fehler`) */
export async function adminFehler(): Promise<Fehlerbericht[]> {
  const r = await apiCall<{ fehler?: Fehlerbericht[] }>(DEFAULT_API, undefined, 'GET', '/admin/fehler');
  return r.fehler ?? [];
}

/** Gleiche Meldungen zusammenfassen: je Meldung der neueste Bericht und wie oft sie vorkam */
export function fehlerZusammenfassen(liste: readonly Fehlerbericht[]): { bericht: Fehlerbericht; anzahl: number }[] {
  const out = new Map<string, { bericht: Fehlerbericht; anzahl: number }>();
  for (const f of [...liste].sort((a, b) => b.at - a.at)) {
    const k = f.nachricht;
    const cur = out.get(k);
    if (cur) cur.anzahl++;
    else out.set(k, { bericht: f, anzahl: 1 });
  }
  return [...out.values()];
}
